import { useSyncExternalStore } from 'react'

/**
 * The deck screen's «Карточки | Тексты» choice, remembered per deck per device
 * (handoff §8).
 *
 * Device state, not session state, like Library's own switch (`mineSegment`): it
 * survives a reload. It is never part of the URL and a swipe never changes it:
 * swipe navigation is off inside a deck, and the deck has its own horizontal
 * gestures (ADR-115 Decision 11).
 *
 * Only decks left on «Тексты» are stored; «Карточки» is the default and an entry
 * for it is removed, so the record holds at most the decks the learner chose to
 * look at as texts.
 */
export type DeckSegment = 'cards' | 'texts'

const KEY = 'cs-deck-segments'
const listeners = new Set<() => void>()
// The record in force for this page load, even when storage is unavailable (a
// private window, blocked site data): a choice then lasts until reload.
let current: Record<string, 'texts'> | null = null

function readStored(): Record<string, 'texts'> {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(KEY) ?? '{}')
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    const clean: Record<string, 'texts'> = {}
    for (const [deckId, value] of Object.entries(parsed)) if (value === 'texts') clean[deckId] = 'texts'
    return clean
  } catch {
    return {}
  }
}

function record(): Record<string, 'texts'> {
  if (current === null) current = readStored()
  return current
}

export function readDeckSegment(deckId: string): DeckSegment {
  return record()[deckId] === 'texts' ? 'texts' : 'cards'
}

export function writeDeckSegment(deckId: string, segment: DeckSegment): void {
  const next = { ...record() }
  if (segment === 'texts') next[deckId] = 'texts'
  else delete next[deckId]
  current = next
  try {
    window.localStorage.setItem(KEY, JSON.stringify(next))
  } catch {
    // Storage refused: kept in memory for this page load only.
  }
  listeners.forEach((l) => l())
}

/** Tests only: forget the in-memory record so the next read goes to storage. */
export function resetDeckSegmentsForTests(): void {
  current = null
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange)
  return () => { listeners.delete(onChange) }
}

export function useDeckSegment(deckId: string | undefined): [DeckSegment, (segment: DeckSegment) => void] {
  const segment = useSyncExternalStore(
    subscribe,
    () => (deckId ? readDeckSegment(deckId) : 'cards'),
    () => 'cards' as DeckSegment,
  )
  const set = (next: DeckSegment) => { if (deckId) writeDeckSegment(deckId, next) }
  return [segment, set]
}
